import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Clock, Briefcase, Users, ArrowRight, Upload,
         X, Check, Loader2, Search, Sparkles, ChevronDown } from "lucide-react";
import axios from "axios";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iso = (s) => {
  if (!s) return "";
  try {
    const d = new Date(s);
    const diff = (Date.now() - d.getTime()) / 86400000;
    if (diff < 1) return "Today";
    if (diff < 2) return "Yesterday";
    if (diff < 14) return `${Math.floor(diff)}d ago`;
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch { return ""; }
};

/* Obsidian-Gold Careers Page — kinetic reveal, Zoho Recruit powered */
export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hint, setHint] = useState("");
  const [query, setQuery] = useState("");
  const [dept, setDept] = useState("all");
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/careers/jobs`);
        setJobs(data.jobs || []);
        setHint(data.hint || "");
      } catch (e) {
        setHint("Careers portal is warming up. Please check back in a minute.");
      } finally { setLoading(false); }
    })();
  }, []);

  const departments = useMemo(() => {
    const set = new Set();
    jobs.forEach(j => { if (j.department) set.add(j.department); });
    return ["all", ...Array.from(set).sort()];
  }, [jobs]);

  const filtered = useMemo(() => {
    return jobs.filter(j => {
      if (dept !== "all" && j.department !== dept) return false;
      if (query) {
        const q = query.toLowerCase();
        const hay = `${j.title} ${j.location} ${j.department} ${j.job_type}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [jobs, query, dept]);

  return (
    <div className="min-h-screen bg-black text-[#F5F5F0]" data-testid="careers-page">
      <Header />

      {/* HERO */}
      <section className="relative pt-40 pb-28 overflow-hidden" data-testid="careers-hero">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(216,178,62,0.10),transparent_60%),radial-gradient(circle_at_80%_80%,rgba(184,115,51,0.08),transparent_60%)]" />
          <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay pointer-events-none"
               style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        </div>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-4xl"
          >
            <div className="font-grotesk text-[10px] uppercase tracking-[0.4em] text-[#8A6A1A] mb-6"
                 data-testid="careers-hero-overline">
              Careers · Vinci Inc.
            </div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight"
                data-testid="careers-hero-title">
              Build what
              <span className="block bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] bg-clip-text text-transparent">
                lasts a century.
              </span>
            </h1>
            <p className="mt-10 font-body text-lg text-[#C9C9C2] max-w-2xl leading-relaxed"
               data-testid="careers-hero-sub">
              We hire quietly. We hire well. If you want your work to move real steel,
              real cement, real cargo across Asia — this is the desk.
            </p>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.9 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl"
          >
            {[
              { k: "Openings", v: loading ? "—" : jobs.length, icon: Briefcase },
              { k: "Locations", v: "Mumbai · Pan-Asia", icon: MapPin },
              { k: "Team", v: "60+ Traders", icon: Users },
              { k: "Since", v: "Est. 2011", icon: Sparkles },
            ].map((s, i) => (
              <div key={i} className="border-l border-[#B87333]/30 pl-5">
                <s.icon className="w-4 h-4 text-[#8A6A1A] mb-3" />
                <div className="font-serif text-2xl text-[#F5F5F0]">{s.v}</div>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A8A82] mt-1">{s.k}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* GILDED DIVIDER */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#D8B23E]/40 to-transparent" />

      {/* JOB BOARD */}
      <section className="py-24 bg-[#050505]" data-testid="careers-board">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
            <div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.4em] text-[#8A6A1A] mb-3">Open Positions</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="careers-board-title">
                Live openings <span className="text-[#8A8A82] text-2xl">· powered by Zoho Recruit</span>
              </h2>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#8A8A82] pointer-events-none" />
                <input
                  data-testid="careers-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search roles…"
                  className="pl-11 pr-4 py-3 bg-transparent border border-white/10 focus:border-[#B87333] outline-none font-body text-sm w-full sm:w-72 transition-colors"
                />
              </div>
              {departments.length > 2 && (
                <div className="relative">
                  <select
                    data-testid="careers-dept-select"
                    value={dept}
                    onChange={(e) => setDept(e.target.value)}
                    className="appearance-none px-4 pr-10 py-3 bg-transparent border border-white/10 focus:border-[#B87333] outline-none font-body text-sm cursor-pointer"
                  >
                    {departments.map(d => <option key={d} value={d} className="bg-black">
                      {d === "all" ? "All departments" : d}
                    </option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A82] pointer-events-none" />
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="py-24 flex justify-center" data-testid="careers-loading">
              <Loader2 className="w-6 h-6 text-[#B87333] animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-24 text-center border border-dashed border-white/10 rounded-sm" data-testid="careers-empty">
              <Briefcase className="w-8 h-8 text-[#8A6A1A] mx-auto mb-4" />
              <h3 className="font-serif text-2xl text-[#F5F5F0] mb-2">
                No openings live right now.
              </h3>
              <p className="font-body text-sm text-[#8A8A82] max-w-md mx-auto">
                {hint || "We keep our roster tight. Write to us at careers@vincitradeasia.com — we read every note."}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-white/5 border-t border-b border-white/5" data-testid="careers-list">
              {filtered.map((j, i) => (
                <motion.li
                  key={j.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`career-row-${j.id}`}
                  className="group py-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-8 cursor-pointer hover:bg-white/[0.015] transition-colors px-4 -mx-4"
                  onClick={() => setSelectedJob(j)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A6A1A] mb-2 flex items-center gap-3">
                      {j.department || "Vinci Team"}
                      {j.job_type && <><span className="w-1 h-1 bg-[#8A6A1A] rounded-full" /><span>{j.job_type}</span></>}
                    </div>
                    <h3 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] group-hover:text-[#F6E4A6] transition-colors">
                      {j.title}
                    </h3>
                  </div>
                  <div className="flex items-center gap-6 text-[#C9C9C2] font-body text-sm md:min-w-[280px]">
                    {j.location && <span className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#8A6A1A]" />{j.location}</span>}
                    {j.posted_at && <span className="flex items-center gap-2 text-[#8A8A82]"><Clock className="w-3.5 h-3.5 text-[#8A6A1A]" />{iso(j.posted_at)}</span>}
                  </div>
                  <div className="flex items-center gap-2 text-[#B87333] font-grotesk text-xs uppercase tracking-[0.28em] group-hover:text-[#F6E4A6] transition-colors">
                    Apply
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </div>
                </motion.li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* WHY US */}
      <section className="py-24 bg-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-12">
            {[
              { title: "The trade you'll actually see move.",
                body: "Every deal you close ships. Every ledger you author becomes an invoice. No paper theatre." },
              { title: "Craft over corporate.",
                body: "We hire fewer, pay better, and let people design their own arc — with Big-Cap sponsorship and a real Board seat as the ceiling." },
              { title: "Legacy over quarter.",
                body: "Vinci was built to outlast us. Your work here is meant to be quoted in 2040." },
            ].map((c, i) => (
              <div key={i} className="border-t border-[#B87333]/30 pt-6">
                <div className="font-grotesk text-[10px] uppercase tracking-[0.4em] text-[#8A6A1A] mb-4">Reason {String(i + 1).padStart(2, "0")}</div>
                <h4 className="font-serif text-2xl text-[#F5F5F0] mb-4 leading-tight">{c.title}</h4>
                <p className="font-body text-sm text-[#C9C9C2] leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <AnimatePresence>
        {selectedJob && (
          <ApplyDrawer job={selectedJob}
                        onClose={() => setSelectedJob(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}


function ApplyDrawer({ job, onClose }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "",
    email: "", phone: "", cover_letter: "",
  });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const setField = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!resume) { setError("Please attach your resume."); return; }
    if (resume.size > 5 * 1024 * 1024) { setError("Resume must be 5 MB or smaller."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("job_id", job.id);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      fd.append("resume", resume);
      const { data } = await axios.post(`${API}/careers/apply`, fd);
      setSuccess(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60]"
        data-testid="apply-drawer-scrim"
      />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 right-0 h-screen w-full sm:w-[560px] bg-[#0A0906] border-l border-[#D8B23E]/30 z-[61] overflow-y-auto"
        data-testid="apply-drawer"
      >
        <div className="h-1 bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333]" />
        <button
          data-testid="apply-drawer-close"
          onClick={onClose}
          className="absolute top-6 right-6 w-9 h-9 grid place-items-center border border-white/10 hover:border-[#B87333] rounded-full transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-8 sm:p-12">
          {success ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
              data-testid="apply-success"
            >
              <div className="w-16 h-16 mx-auto rounded-full border border-[#2FB999] grid place-items-center mb-6">
                <Check className="w-7 h-7 text-[#2FB999]" />
              </div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.4em] text-[#8A6A1A] mb-4">Application received</div>
              <h3 className="font-serif text-3xl text-[#F5F5F0] mb-4">
                Thank you.
              </h3>
              <p className="font-body text-sm text-[#C9C9C2] max-w-sm mx-auto leading-relaxed">
                We received your application for <span className="text-[#F6E4A6]">{success.job_title || job.title}</span>.
                A confirmation just landed in your inbox. Our team will review your profile and respond within 7 working days.
              </p>
              <button
                data-testid="apply-success-close"
                onClick={onClose}
                className="mt-10 inline-flex items-center gap-2 rounded-full border border-[#B87333]/60 px-6 py-3 font-grotesk text-xs uppercase tracking-[0.28em] hover:bg-[#B87333] hover:text-black transition-colors"
              >
                Return to careers
              </button>
            </motion.div>
          ) : (
            <>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.4em] text-[#8A6A1A] mb-4">
                {job.department || "Vinci Team"}
              </div>
              <h2 className="font-serif text-3xl text-[#F5F5F0] leading-tight" data-testid="apply-job-title">
                {job.title}
              </h2>
              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-[#8A8A82] font-body text-xs">
                {job.location && <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{job.location}</span>}
                {job.job_type && <span>{job.job_type}</span>}
                {job.work_experience && <span>{job.work_experience}</span>}
              </div>

              {job.description && (
                <div className="mt-8 max-h-56 overflow-y-auto pr-2 border-l-2 border-[#B87333]/30 pl-4">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A6A1A] mb-3">About the role</div>
                  <div
                    className="font-body text-sm text-[#C9C9C2] leading-relaxed whitespace-pre-wrap"
                    data-testid="apply-job-description"
                    dangerouslySetInnerHTML={{ __html: job.description }}
                  />
                </div>
              )}

              <form onSubmit={submit} className="mt-10 space-y-5" data-testid="apply-form">
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="First name *" value={form.first_name} onChange={setField("first_name")} required testid="apply-first-name" />
                  <FormField label="Last name" value={form.last_name} onChange={setField("last_name")} testid="apply-last-name" />
                </div>
                <FormField label="Email *" type="email" value={form.email} onChange={setField("email")} required testid="apply-email" />
                <FormField label="Phone" value={form.phone} onChange={setField("phone")} testid="apply-phone" />
                <div>
                  <label className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A6A1A] block mb-2">
                    Cover note (optional)
                  </label>
                  <textarea
                    data-testid="apply-cover-letter"
                    value={form.cover_letter}
                    onChange={setField("cover_letter")}
                    rows={4}
                    placeholder="Why Vinci? Two lines are enough."
                    className="w-full bg-transparent border border-white/10 focus:border-[#B87333] outline-none font-body text-sm px-4 py-3 transition-colors resize-none"
                  />
                </div>
                <ResumeField file={resume} onChange={setResume} />

                {error && (
                  <div className="border border-[#C0392B]/40 bg-[#C0392B]/10 px-4 py-3 text-[#F8B5A8] font-body text-sm rounded-sm"
                       data-testid="apply-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="apply-submit"
                  className="w-full mt-4 inline-flex items-center justify-center gap-3 py-4 bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk text-xs uppercase tracking-[0.32em] font-medium hover:shadow-[0_0_40px_rgba(216,178,62,0.35)] transition-shadow disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Submitting</> : <>Submit Application<ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-center font-body text-[10px] text-[#6f6f68] mt-3 leading-relaxed">
                  Your resume is uploaded securely to Vinci's recruitment system.
                  We never share your details with third parties.
                </p>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </>
  );
}

function FormField({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A6A1A] block mb-2">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type} value={value} onChange={onChange} required={required}
        className="w-full bg-transparent border border-white/10 focus:border-[#B87333] outline-none font-body text-sm px-4 py-3 transition-colors"
      />
    </div>
  );
}

function ResumeField({ file, onChange }) {
  return (
    <div>
      <label className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A6A1A] block mb-2">
        Resume *
      </label>
      <label
        htmlFor="career-resume"
        data-testid="apply-resume-drop"
        className={`flex items-center gap-4 border-2 border-dashed ${file ? "border-[#B87333]/60 bg-[#B87333]/5" : "border-white/10 hover:border-[#B87333]/40"} px-5 py-4 cursor-pointer transition-colors`}
      >
        <Upload className="w-5 h-5 text-[#B87333]" />
        <div className="flex-1 min-w-0">
          {file ? (
            <>
              <div className="font-body text-sm text-[#F5F5F0] truncate">{file.name}</div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] mt-1">
                {(file.size / 1024).toFixed(0)} KB · click to replace
              </div>
            </>
          ) : (
            <>
              <div className="font-body text-sm text-[#F5F5F0]">Attach your resume</div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] mt-1">
                PDF · DOC · DOCX · up to 5 MB
              </div>
            </>
          )}
        </div>
      </label>
      <input
        id="career-resume"
        data-testid="apply-resume-input"
        type="file"
        accept=".pdf,.doc,.docx,.rtf,.odt"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        className="hidden"
      />
    </div>
  );
}
